import { Box, Button, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material'
import { KoreanLeadingSchoolType } from 'Entities/koreanLeadingSchool'
import { SectionLoader } from 'Components/Section'
import { useMemo, useState } from 'react'
import { useKoreanLanguage2 } from '../Hooks/useKoreanLanguageForm'
import { useKoreanLeadingSchools } from '../Hooks/useKoreanLeadingSchools'

type FormState = {
  type: KoreanLeadingSchoolType | ''
  regionCode: number
  schoolNameUz: string
  schoolNameRu: string
  schoolNameKo: string
}

type Props = {
  onCancel: () => void
}

const initialForm: FormState = {
  type: '',
  regionCode: 0,
  schoolNameUz: '',
  schoolNameRu: '',
  schoolNameKo: '',
}

const TYPE_OPTIONS: { value: KoreanLeadingSchoolType; label: string }[] = [
  { value: 'LEADING_SCHOOL', label: 'Образцовая школа' },
  { value: 'KOREAN_CULTURE_SPECIALIZED_SCHOOL', label: 'Школа углубленного изучения культуры Кореи' },
]

export const KoreanLeadingSchoolCreateForm = ({ onCancel }: Props) => {
  const [form, setForm] = useState<FormState>(initialForm)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const { regions } = useKoreanLanguage2()
  const { createLeadingSchoolMutation } = useKoreanLeadingSchools()

  const errors = useMemo(
    () => ({
      type: !form.type,
      regionCode: !form.regionCode,
      schoolNameUz: !form.schoolNameUz.trim(),
      schoolNameRu: !form.schoolNameRu.trim(),
      schoolNameKo: !form.schoolNameKo.trim(),
    }),
    [form],
  )

  const hasError = errors.type || errors.regionCode || errors.schoolNameUz || errors.schoolNameRu || errors.schoolNameKo

  const handleChange = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setForm(prev => ({ ...prev, [field]: field === 'regionCode' ? Number(value) : value }))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitAttempted(true)

    if (hasError || !form.type) return

    createLeadingSchoolMutation.mutate(
      {
        type: form.type,
        regionCode: form.regionCode,
        schoolNameUz: form.schoolNameUz.trim(),
        schoolNameRu: form.schoolNameRu.trim(),
        schoolNameKo: form.schoolNameKo.trim(),
      },
      {
        onSuccess: () => {
          setForm(initialForm)
          setSubmitAttempted(false)
        },
      },
    )
  }

  return (
    <SectionLoader isLoading={false} isFetching={false}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', backgroundColor: '#fff' }}>
        <Stack spacing={3} component='form' onSubmit={handleSubmit}>
          <Box>
            <Typography variant='h5' fontWeight={700}>
              Добавить образцовую школу
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label='Тип школы'
                value={form.type}
                onChange={handleChange('type')}
                error={submitAttempted && errors.type}
                helperText={submitAttempted && errors.type ? 'Выберите тип.' : ' '}>
                {TYPE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label='Выберите регион'
                value={form.regionCode || ''}
                onChange={handleChange('regionCode')}
                error={submitAttempted && errors.regionCode}
                helperText={submitAttempted && errors.regionCode ? 'Выберите регион.' : ' '}>
                {regions.map((region: { regionCode: number; regionName: string }) => (
                  <MenuItem key={region.regionCode} value={region.regionCode}>
                    {region.regionName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Название (UZ)'
                value={form.schoolNameUz}
                onChange={handleChange('schoolNameUz')}
                error={submitAttempted && errors.schoolNameUz}
                helperText={submitAttempted && errors.schoolNameUz ? 'Обязательное поле.' : ' '}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Название (RU)'
                value={form.schoolNameRu}
                onChange={handleChange('schoolNameRu')}
                error={submitAttempted && errors.schoolNameRu}
                helperText={submitAttempted && errors.schoolNameRu ? 'Обязательное поле.' : ' '}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Название (KO)'
                value={form.schoolNameKo}
                onChange={handleChange('schoolNameKo')}
                error={submitAttempted && errors.schoolNameKo}
                helperText={submitAttempted && errors.schoolNameKo ? 'Обязательное поле.' : ' '}
              />
            </Grid>
          </Grid>

          <Stack direction='row' spacing={1.5} justifyContent='flex-end'>
            <Button variant='outlined' color='inherit' onClick={onCancel} disabled={createLeadingSchoolMutation.isLoading}>
              Отмена
            </Button>
            <Button type='submit' variant='contained' disabled={hasError || createLeadingSchoolMutation.isLoading}>
              {createLeadingSchoolMutation.isLoading ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </SectionLoader>
  )
}
